import { useState, useMemo, useCallback } from 'react';
import { cars } from '../data/cars';

export const useCarFilters = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedFuel, setSelectedFuel] = useState('all');
  const [selectedTransmission, setSelectedTransmission] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minKm, setMinKm] = useState('');
  const [maxKm, setMaxKm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [expandedFilters, setExpandedFilters] = useState({
    brand: false,
    year: false,
    fuel: false,
    transmission: false,
    kilometers: false,
    price: false
  });

  // Memoized filter options
  const filterOptions = useMemo(() => ({
    brands: ['all', ...Array.from(new Set(cars.map(car => car.brand)))],
    years: ['all', ...Array.from(new Set(cars.map(car => car.year))).sort((a, b) => b - a)],
    fuels: ['all', ...Array.from(new Set(cars.map(car => car.fuel)))],
    transmissions: ['all', ...Array.from(new Set(cars.map(car => car.transmission)))]
  }), []);

  // Get count for each filter option (memoized)
  const getFilterCount = useCallback((type, value) => {
    return cars.filter(car => {
      const matchesSearch = car.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           car.model.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matches = true;
      if (selectedBrand !== 'all' && type !== 'brand') matches = matches && car.brand === selectedBrand;
      if (selectedYear !== 'all' && type !== 'year') matches = matches && car.year === parseInt(selectedYear);
      if (selectedFuel !== 'all' && type !== 'fuel') matches = matches && car.fuel === selectedFuel;
      if (selectedTransmission !== 'all' && type !== 'transmission') matches = matches && car.transmission === selectedTransmission;
      if ((minKm || maxKm) && type !== 'kilometers') {
        const matchesMinKm = !minKm || car.kilometers >= parseInt(minKm);
        const matchesMaxKm = !maxKm || car.kilometers <= parseInt(maxKm);
        matches = matches && matchesMinKm && matchesMaxKm;
      }
      if ((minPrice || maxPrice) && type !== 'price') {
        const matchesMinPrice = !minPrice || car.price >= parseInt(minPrice);
        const matchesMaxPrice = !maxPrice || car.price <= parseInt(maxPrice);
        matches = matches && matchesMinPrice && matchesMaxPrice;
      }
      
      if (type === 'brand') matches = matches && car.brand === value;
      if (type === 'year') matches = matches && car.year === parseInt(value);
      if (type === 'fuel') matches = matches && car.fuel === value;
      if (type === 'transmission') matches = matches && car.transmission === value;
      
      return matches;
    }).length;
  }, [searchTerm, selectedBrand, selectedYear, selectedFuel, selectedTransmission, minKm, maxKm, minPrice, maxPrice]);

  // Memoized filtered and sorted cars
  const filteredAndSortedCars = useMemo(() => {
    let filtered = cars.filter(car => {
      // Exclude sold cars when any filter is applied (except search)
      const hasActiveFilters = selectedBrand !== 'all' || selectedYear !== 'all' || 
                             selectedFuel !== 'all' || selectedTransmission !== 'all' ||
                             minPrice || maxPrice || minKm || maxKm;
      
      if (hasActiveFilters && car.sold) return false;
      
      // Search filtering
      const matchesSearch = car.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           car.model.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Brand filtering (single selection)
      const matchesBrand = selectedBrand === 'all' || car.brand === selectedBrand;
      
      // Year filtering (single selection)
      const matchesYear = selectedYear === 'all' || car.year === selectedYear;
      
      // Fuel filtering (single selection)
      const matchesFuel = selectedFuel === 'all' || car.fuel === selectedFuel;
      
      // Transmission filtering (single selection)
      const matchesTransmission = selectedTransmission === 'all' || car.transmission === selectedTransmission;
      
      // Fixed price filtering
      const matchesMinPrice = !minPrice || car.price >= parseInt(minPrice);
      const matchesMaxPrice = !maxPrice || car.price <= parseInt(maxPrice);
      
      // Kilometers filtering
      const matchesMinKm = !minKm || car.kilometers >= parseInt(minKm);
      const matchesMaxKm = !maxKm || car.kilometers <= parseInt(maxKm);
      
      return matchesSearch && matchesBrand && matchesYear && matchesFuel && 
             matchesTransmission && matchesMinPrice && matchesMaxPrice && matchesMinKm && matchesMaxKm;
    });

    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => {
          // First sort by sold status, then by price
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return a.price - b.price;
        });
        break;
      case 'price-desc':
        filtered.sort((a, b) => {
          // First sort by sold status, then by price
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return b.price - a.price;
        });
        break;
      case 'year-desc':
        filtered.sort((a, b) => {
          // First sort by sold status, then by year
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return b.year - a.year;
        });
        break;
      case 'year-asc':
        filtered.sort((a, b) => {
          // First sort by sold status, then by year
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return a.year - b.year;
        });
        break;
      case 'km-desc':
        filtered.sort((a, b) => {
          // First sort by sold status, then by kilometers
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return b.kilometers - a.kilometers;
        });
        break;
      case 'km-asc':
        filtered.sort((a, b) => {
          // First sort by sold status, then by kilometers
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return a.kilometers - b.kilometers;
        });
        break;
      default:
        // Sort by sold status first (available cars first), then by ID
        filtered.sort((a, b) => {
          if (a.sold !== b.sold) return a.sold ? 1 : -1;
          return a.id - b.id; // Keep original order by ID for available cars
        });
        break;
    }

    return filtered;
  }, [searchTerm, selectedBrand, selectedYear, selectedFuel, selectedTransmission, minPrice, maxPrice, minKm, maxKm, sortBy]);

  // Get active filters for display
  const getActiveFilters = useCallback(() => {
    const formatPrice = (value) => {
      if (!value) return '';
      return parseInt(value).toLocaleString('es-AR');
    };

    const active = [];
    if (searchTerm) active.push({ type: 'search', value: searchTerm, label: `Búsqueda: "${searchTerm}"` });
    if (selectedBrand !== 'all') active.push({ type: 'brand', value: selectedBrand, label: `Marca: ${selectedBrand}` });
    if (selectedYear !== 'all') active.push({ type: 'year', value: selectedYear, label: `Año: ${selectedYear}` });
    if (selectedFuel !== 'all') active.push({ type: 'fuel', value: selectedFuel, label: `Combustible: ${selectedFuel}` });
    if (selectedTransmission !== 'all') active.push({ type: 'transmission', value: selectedTransmission, label: `Transmisión: ${selectedTransmission}` });
    if (minKm || maxKm) active.push({ type: 'kilometers', value: `${minKm}-${maxKm}`, label: `Km: ${minKm || '0'} - ${maxKm || '∞'}` });
    if (minPrice || maxPrice) active.push({ type: 'price', value: `${minPrice}-${maxPrice}`, label: `Precio: $${formatPrice(minPrice)} - $${formatPrice(maxPrice)}` });
    return active;
  }, [searchTerm, selectedBrand, selectedYear, selectedFuel, selectedTransmission, minKm, maxKm, minPrice, maxPrice]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedYear('all');
    setSelectedFuel('all');
    setSelectedTransmission('all');
    setMinPrice('');
    setMaxPrice('');
    setMinKm('');
    setMaxKm('');
  }, []);

  const removeFilter = useCallback((type, value) => {
    switch (type) {
      case 'search':
        setSearchTerm('');
        break;
      case 'brand':
        setSelectedBrand('all');
        break;
      case 'year':
        setSelectedYear('all');
        break;
      case 'fuel':
        setSelectedFuel('all');
        break;
      case 'transmission':
        setSelectedTransmission('all');
        break;
      case 'kilometers':
        setMinKm('');
        setMaxKm('');
        break;
      case 'price':
        setMinPrice('');
        setMaxPrice('');
        break;
    }
  }, []);

  const toggleFilter = useCallback((filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  }, []);

  const closeFilter = useCallback((filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: false
    }));
  }, []);

  return {
    // State
    searchTerm,
    setSearchTerm,
    selectedBrand,
    setSelectedBrand,
    selectedYear,
    setSelectedYear,
    selectedFuel,
    setSelectedFuel,
    selectedTransmission,
    setSelectedTransmission,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    minKm,
    setMinKm,
    maxKm,
    setMaxKm,
    sortBy,
    setSortBy,
    expandedFilters,
    
    // Computed values
    filterOptions,
    filteredCars: filteredAndSortedCars,
    activeFilters: getActiveFilters(),
    
    // Actions
    clearFilters,
    removeFilter,
    toggleFilter,
    closeFilter,
    getFilterCount
  };
};
